/**
 * API Client with retry logic and error handling
 * Supports both ai360api (simple) and ai360agency (advanced) backends
 */

import type {
  ApiClientConfig,
  SearchOptions,
  SearchResponse,
  TranscriptResponse,
  AudioResponse,
  ChatResponse,
  Message,
} from '../types';
import type { UnifiedResponse, SearchResult } from '@ai360smartsearch/shared';

type ApiType = 'ai360api' | 'ai360agency';

class ApiClient {
  private baseUrl: string;
  private apiKey?: string;
  private apiType: ApiType;
  private timeout: number;
  private retries: number;

  constructor(config: ApiClientConfig) {
    this.baseUrl = this.normalizeBaseUrl(config.baseUrl);
    this.apiKey = config.apiKey;
    this.apiType = config.apiType || 'ai360api';
    this.timeout = config.timeout || 30000;
    this.retries = config.retries || 3;
  }

  /**
   * Normalize backend URL to remove any /wp-json/ paths
   */
  private normalizeBaseUrl(url: string): string {
    let normalized = url.replace(/\/+$/, ''); // Remove trailing slashes
    
    // If URL contains /wp-json/, extract only the base
    if (normalized.includes('/wp-json/')) {
      normalized = normalized.replace(/\/wp-json\/.*$/, '');
    }
    
    return normalized;
  }

  /**
   * Get the appropriate endpoint based on API type
   */
  private getEndpoint(action: 'search' | 'chat' | 'transcribe' | 'speak'): string {
    // Get endpoint mode from WordPress settings
    const wpSettings = (window as any).ai360smartsearchSettings || {};
    const endpointMode = wpSettings.endpointMode || 'search';
    
    if (this.apiType === 'ai360agency') {
      // ai360agency endpoints
      const endpoints: Record<string, string> = {
        search: '/wp-json/ai360agency/v1/search',
        chat: '/wp-json/ai360agency/v1/chat',
        transcribe: '/wp-json/ai360agency/v1/voice/transcribe',
        speak: '/wp-json/ai360agency/v1/voice/speak',
      };
      return `${this.baseUrl}${endpoints[action]}`;
    } else {
      // ai360api - use endpoint according to configured mode
      const searchEndpoint = endpointMode === 'chat' 
        ? '/wp-json/360groupai/v1/chat'
        : '/wp-json/360groupai/v1/search';

      const endpoints: Record<string, string> = {
        search: searchEndpoint,  // Use configured endpoint
        chat: '/wp-json/360groupai/v1/chat',
        transcribe: '/wp-json/360groupai/v1/chat',
        speak: '/wp-json/360groupai/v1/chat',
      };
      return `${this.baseUrl}${endpoints[action]}`;
    }
  }

  /**
   * Get headers with proper authorization
   */
  private getHeaders(additionalHeaders?: Record<string, string>): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.apiKey) {
      headers['Authorization'] = `Bearer ${this.apiKey}`;
    }

    return { ...headers, ...additionalHeaders };
  }

  private async fetchWithRetry(
    url: string,
    options: RequestInit,
    attempt = 0
  ): Promise<Response> {
    // CRITICAL: Check max retries FIRST to prevent infinite loops
    if (attempt >= this.retries) {
      console.error(`🚫 Max retries (${this.retries}) exceeded for ${url}`);
      throw new Error(`Max retries (${this.retries}) exceeded`);
    }

    console.log(`🔍 FETCH URL (attempt ${attempt + 1}/${this.retries}): ${url}`);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        headers: this.getHeaders(options.headers as Record<string, string>),
      });

      clearTimeout(timeoutId);

      console.log(`📡 Response status: ${response.status} ${response.statusText}`);

      // Only retry on server errors (5xx), NOT on client errors (4xx)
      if (!response.ok) {
        if (response.status >= 500 && attempt < this.retries - 1) {
          console.warn(`⚠️ Server error ${response.status}, retrying... (attempt ${attempt + 1}/${this.retries})`);
          // Exponential backoff: 1s, 2s, 4s
          const delay = Math.pow(2, attempt) * 1000;
          await new Promise(resolve => setTimeout(resolve, delay));
          return this.fetchWithRetry(url, options, attempt + 1);
        }
        // For 4xx errors or max retries reached, return response as-is (don't retry)
        console.log(`⛔ ${response.status >= 500 ? 'Max retries reached' : 'Client error'}, not retrying`);
        return response;
      }

      console.log(`✅ Request successful`);
      return response;
    } catch (error: any) {
      console.error(`❌ ERROR (attempt ${attempt + 1}/${this.retries}):`, error.message || error);
      
      if (error.name === 'AbortError') {
        // Timeout errors should not be retried indefinitely
        if (attempt < this.retries - 1) {
          console.warn(`⏱️ Request timeout, retrying... (attempt ${attempt + 1}/${this.retries})`);
          const delay = Math.pow(2, attempt) * 1000;
          await new Promise(resolve => setTimeout(resolve, delay));
          return this.fetchWithRetry(url, options, attempt + 1);
        }
        console.error(`🚫 Request timeout after ${this.retries} attempts`);
        throw new Error('Request timeout');
      }

      // For other network errors, retry if under limit
      if (attempt < this.retries - 1) {
        console.warn(`🔄 Network error, retrying... (attempt ${attempt + 1}/${this.retries})`);
        const delay = Math.pow(2, attempt) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.fetchWithRetry(url, options, attempt + 1);
      }

      console.error(`🚫 Max retries reached, giving up`);
      throw error;
    }
  }

  /**
   * Raw search - returns unified response format
   */
  async searchRaw(query: string, options?: SearchOptions): Promise<UnifiedResponse> {
    const url = this.getEndpoint('search');
    
    // Get endpoint mode and default limit from WordPress settings
    const wpSettings = (window as any).ai360smartsearchSettings || {};
    const endpointMode = wpSettings.endpointMode || 'search';
    const defaultLimit = wpSettings.minResults || wpSettings.results?.minResults || 10;
    
    // Build request body based on API type and endpoint mode
    let body: any;
    
    if (this.apiType === 'ai360agency') {
      body = { query, maxResults: options?.maxResults, filters: options?.filters };
    } else if (endpointMode === 'search') {
      // Endpoint /search uses "query" and "limit"
      body = { 
        query: query, 
        limit: options?.maxResults || defaultLimit,
        post_types: ['post', 'page', 'product']  // Add 'product' for WooCommerce support
      };
    } else {
      // Endpoint /chat uses "question"
      body = { question: query };
    }

    const response = await this.fetchWithRetry(url, {
      method: 'POST',
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || `Search failed: ${response.status}`);
    }

    const data = await response.json();
    
    // Normalize response to unified format
    return this.normalizeToUnifiedResponse(data, query);
  }

  /**
   * Search - returns SearchResponse for backward compatibility
   */
  async search(query: string, options?: SearchOptions): Promise<SearchResponse> {
    const unifiedResponse = await this.searchRaw(query, options);
    
    return {
      query: unifiedResponse.meta.query,
      results: unifiedResponse.results,
      summary: unifiedResponse.response || undefined,
      totalResults: unifiedResponse.meta.total,
    };
  }

  /**
   * Normalize various response formats to unified format
   */
  private normalizeToUnifiedResponse(data: any, query: string): UnifiedResponse {
    // Check if already in unified format
    if (data.type && (data.type === 'chat' || data.type === 'search') && data.results && data.meta) {
      return data as UnifiedResponse;
    }

    // Check if it's the new format with results array
    if (data.results && Array.isArray(data.results)) {
      return {
        type: data.type || 'search',
        response: data.response || null,
        results: data.results,
        meta: data.meta || {
          query: query,
          total: data.results.length,
          model: data.model,
          tokens_used: data.tokens_used,
        },
      };
    }

    // Old format - text response only
    if (data.response || data.answer) {
      const responseText = data.response || data.answer;
      // Generate more robust ID using timestamp and random hex string
      const timestamp = Date.now();
      const randomHex = Array.from(crypto.getRandomValues(new Uint8Array(4)))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
      
      return {
        type: 'chat',
        response: responseText,
        results: [{
          id: `chat-${timestamp}-${randomHex}`,
          type: 'chat',
          title: 'Respuesta',
          content: responseText,
          snippet: responseText,
          url: null,
          trustScore: 0,
        }],
        meta: {
          query: query,
          total: 1,
          model: data.model,
        },
      };
    }

    // Fallback - empty response
    return {
      type: 'search',
      response: null,
      results: [],
      meta: {
        query: query,
        total: 0,
      },
    };
  }

  async transcribe(audio: Blob): Promise<TranscriptResponse> {
    const url = this.getEndpoint('transcribe');
    const formData = new FormData();
    formData.append('audio', audio);

    const response = await this.fetchWithRetry(url, {
      method: 'POST',
      body: formData,
      headers: {
        ...(this.apiKey && { 'Authorization': `Bearer ${this.apiKey}` }),
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || `Transcription failed: ${response.status}`);
    }

    return response.json();
  }

  async speak(text: string, language?: string): Promise<AudioResponse> {
    const url = this.getEndpoint('speak');
    const response = await this.fetchWithRetry(url, {
      method: 'POST',
      body: JSON.stringify({ text, language }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || `TTS failed: ${response.status}`);
    }

    const audioBlob = await response.blob();
    return { audioData: audioBlob };
  }

  async chat(messages: Message[]): Promise<ChatResponse> {
    const url = this.getEndpoint('chat');
    const lastMessage = messages[messages.length - 1]?.content || '';
    
    const body = this.apiType === 'ai360agency'
      ? { message: lastMessage, providerId: 'auto' }
      : { question: lastMessage };

    const response = await this.fetchWithRetry(url, {
      method: 'POST',
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || `Chat failed: ${response.status}`);
    }

    const data = await response.json();
    
    // Normalize response
    if (this.apiType === 'ai360api') {
      return {
        message: data.response || data.answer || '',
        response: data.response || data.answer || '',
        sources: data.sources || [],
      };
    }
    
    return data;
  }

  async getInventory(): Promise<any[]> {
    // For ai360api, we might not have inventory endpoint
    if (this.apiType === 'ai360api') {
      console.warn('Inventory endpoint not supported in ai360api mode. Switch to ai360agency for inventory functionality.');
      return [];
    }
    
    const url = `${this.baseUrl}/wp-json/ai360agency/v1/inventory`;
    const response = await this.fetchWithRetry(url, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || `Inventory fetch failed: ${response.status}`);
    }

    const data = await response.json();
    return data.items || [];
  }

  async resolveNavigation(utterance: string): Promise<any> {
    if (this.apiType === 'ai360api') {
      // Use chat endpoint for navigation in simple mode
      return this.chat([{ role: 'user', content: utterance }]);
    }
    
    const url = `${this.baseUrl}/wp-json/ai360agency/v1/navigation/resolve`;
    const response = await this.fetchWithRetry(url, {
      method: 'POST',
      body: JSON.stringify({ utterance }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || `Navigation failed: ${response.status}`);
    }

    return response.json();
  }
}

let apiClientInstance: ApiClient | null = null;

export function createApiClient(config: ApiClientConfig): ApiClient {
  apiClientInstance = new ApiClient(config);
  return apiClientInstance;
}

export function getApiClient(): ApiClient {
  if (!apiClientInstance) {
    throw new Error('API Client not initialized. Call createApiClient first.');
  }
  return apiClientInstance;
}

export { ApiClient };
